import { LocalPolice } from "@mui/icons-material";
import { Card, Grid } from "@mui/material";
import CustomTitle from "components/shared/CustomTitle";
import MyTable from "components/shared/Tables/MyTable/MyTable";
import MyTableToolbar from "components/shared/Tables/MyTable/MyTableToolbar";
import { handleDownloadCsv, handlePrintCsv } from "components/utils/handlers";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { alertsActions } from "../../../redux/slice/alerts.slice";
import { analyticsActions } from "../../../redux/slice/analytics.slice";
import { authActions } from "../../../redux/slice/auth.slice";
import { devicesActions } from "../../../redux/slice/devices.slice";
import { groupSelectors } from "../../../redux/slice/group.slice";
import { GET_DEVICES, GET_DEVICES_LOCATION } from "../../../redux/types/devices.types";
import { GET_ACCESS_PRIVILEGE, GET_ALL_ALERTS, GET_HIGH_RISK_DEVICES, GET_USER_ROLES } from "../../../redux/types/multi.types";
import table from '../../shared/Tables/table.module.scss';
import theme from "../../utils/theme";
import DeviceMap from "../Device/DeviceMap/DeviceMap";
import { getMarker } from "../Device/helper";
import DeviceStatusChart from "./DeviceStatusChart";
import { HIGH_RISK_DOWNLOAD_COLS, highRiskColumns, WarningIcon } from "./helper";
import PrivilegeAccess from "./PrivilegeAccess";
import ScatterChart from "./ScatterChart";
import SecurityAlerts from "./SecurityAlerts";
import ThreatComparison from "./ThreatComparison";
import ThreatDetection from "./ThreatDetection";
import UserRoleDistribution from "./UserRoleDistribution";

const MultiDashboard = () => {
  const dispatch = useDispatch();
  const currentUser = useSelector(authActions.selectors.getCurrentUser)
  useEffect(() => {
    const payload = {
      orgId: currentUser?.orgId,
    };
    dispatch({ type: GET_DEVICES });
    dispatch({ type: GET_DEVICES_LOCATION });
    dispatch({ type: GET_HIGH_RISK_DEVICES, payload });
  }, [currentUser?.orgId, dispatch]);


  const devices = useSelector(devicesActions.selectors.getDevices);
  const devicesLocs = useSelector(devicesActions.selectors.getDeviceLocation);

  const markers = useMemo(() => {
    if (devicesLocs?.length) {
      return devicesLocs.map((device) => {
        const loc = device?.loc;
        if (loc) {
          const coordinates = loc.split(",").map(Number)?.reverse();
          const html = getMarker(theme.palette.success.main);
          return { coordinates, html, device: device?.device };
        }
        return null
      })
    }
  }, [devicesLocs]);

  const allAlerts = useSelector(alertsActions.selectors.getAllAlerts)
  const accessPrivilege = useSelector(groupSelectors.getAccessPrivilege)
  const userRoles = useSelector(analyticsActions.selectors.getUserRoles)
  const highRiskDevices = useSelector(analyticsActions.selectors.getHighRiskDevices)
  useEffect(() => {
    dispatch({ type: GET_ALL_ALERTS })
    dispatch({ type: GET_ACCESS_PRIVILEGE })
    dispatch({ type: GET_USER_ROLES })
  }, [dispatch]);


  const [searchTerm, setSearchTerm] = useState("");

  const handleSearch = (term) => {
    setSearchTerm(term);
  };

  const filteredDevices = useMemo(() => {
    if (!searchTerm) return highRiskDevices;
    const regex = new RegExp(searchTerm, "i");
    return highRiskDevices.filter((device) =>
      Object.values(device).some((value) => regex.test(value))
    );
  }, [highRiskDevices, searchTerm]);

  return (
    <div>
      <Grid
        container
        sx={{ justifyContent: { xl: "space-between", md: "center" } }}
        spacing={2}
      >
        <Grid my={"5px"} container spacing={2}>
          <Grid item md={12} lg={6}>
            <ScatterChart />
          </Grid>
          <Grid item xs={12} md={12} lg={6}>
            <ThreatDetection />
          </Grid>
        </Grid>
        <Grid item xs={12} md={6} lg={6}>
          <DeviceMap markers={markers} center={devicesLocs?.[0]?.loc} />
        </Grid>
        <Grid item xs={12} md={12} lg={6}>
          <ThreatComparison alerts={allAlerts?.chartData} />
        </Grid>
        <Grid item xs={12} md={12} lg={6}>
          <DeviceStatusChart devices={devices} />
        </Grid>
        <Grid item xs={12} md={12} lg={6}>
          <SecurityAlerts alerts={allAlerts} />
        </Grid>
        <Grid item xs={12} md={12} lg={6}>
          <PrivilegeAccess accessPrivilege={accessPrivilege} />
        </Grid>
        <Grid item xs={12} md={12} lg={6}>
          <UserRoleDistribution userRoles={userRoles} />
        </Grid>
        <Grid item xs={12} className={table.tableContainer}>
          <CustomTitle title={"High Risk Devices"} count={highRiskDevices?.length} />
          <Card>
            <MyTableToolbar
              placeHolder={"Search For High Risk Devices"}
              onSearch={handleSearch}
              onDownload={() => handleDownloadCsv({ rows: highRiskDevices, columns: HIGH_RISK_DOWNLOAD_COLS, name: "High_Risk_Devices" })}
              onPrint={() => handlePrintCsv({ rows: highRiskDevices, columns: HIGH_RISK_DOWNLOAD_COLS, name: "High_Risk_Devices" })}
              btn2Txt={"Quarantine Device"}
              btn2Icon={LocalPolice}
              btn1Txt={"Issue Warning"}
              btn1Icon={WarningIcon}
            />
            <MyTable
              checkboxSelection={true}
              columns={highRiskColumns}
              rows={filteredDevices}
              id={(row) => row?._id}
            />
          </Card>
        </Grid>

      </Grid>


      <br />
    </div>
  );
};

export default MultiDashboard;
